import { useCallback, useContext, useEffect, useState } from "react";
import { SignFormContext } from "../../SignForm";
import api from "../../../../../../service/api";
import useStudentResource from "../../../../BcaAttendance/hooks/useStudentResource";
import handleSignatureSubmit from "./handleSignatureSubmit";
import download from "downloadjs";

export default function useSignFormPayload() {
    const {
        batchData,
        studentDataArray,
        studentDropdownData,
        pdfTemplateDropdownData,
        pdfUrl,
        setPdfUrl,
        batch_student_id,
        setBatchStudentId,
        template_id,
        setTemplateId,
        payload,
        student_id,
        setStudentId,
        s3Link,
        setS3Link,
        requestRefresh,
        setRequestRefresh,
    } = useContext(SignFormContext) ?? {};

    const [mergedPdfUrl, setMergedPdfUrl] = useState(null);
    const [signedStudentIds, setSignedStudentIds] = useState([]);
    const [loadingMergedPdf, setLoadingMergedPdf] = useState(false);
    const [mergedPdfError, setMergedPdfError] = useState(null);
    const [loadingSignedDocs, setLoadingSignedDocs] = useState(false);

    const studentResource = useStudentResource({ id: null });
    const { handleSubmit, loadSignatures } = handleSignatureSubmit({
        id: payload?.batch_student_id ?? null,
        setRequestRefresh,
    });

    const handleTemplateSelection = useCallback(
        (selection) => {
            const value = !selection
                ? null
                : selection.value != ""
                ? selection.value
                : null;
            setTemplateId(value);
        },
        [setTemplateId]
    );

    const handleStudentSelection = useCallback(
        (selection) => {
            const value = !selection
                ? null
                : selection.value != ""
                ? selection.value
                : null;
            setBatchStudentId(value);
        },
        [setBatchStudentId]
    );

    useEffect(() => {
        if (!batch_student_id || !template_id) {
            setS3Link(null);
        }

        if (batch_student_id) {
            const student_data = studentDataArray.find(
                (value) => value.id == batch_student_id
            );
            setStudentId(student_data?.student?.id ?? null);
        }

        if (template_id && batch_student_id) {
            fetchPdf();
            gets3PdfLink();
        }
    }, [template_id, batch_student_id]);

    useEffect(() => {
        if (mergedPdfUrl) {
            URL.revokeObjectURL(mergedPdfUrl);
        }
        setMergedPdfUrl(null);
        setSignedStudentIds([]);
        setMergedPdfError(null);

        if (template_id && studentDropdownData?.length) {
            checkSignedDocuments();
        }
    }, [template_id]);

    useEffect(() => {
        if (requestRefresh) {
            fetchPdf();
            gets3PdfLink();
            if (template_id && studentDropdownData?.length) {
                checkSignedDocuments();
            }
            setRequestRefresh(false);
        }
    }, [requestRefresh]);

    useEffect(() => {
        return () => {
            if (mergedPdfUrl) {
                URL.revokeObjectURL(mergedPdfUrl);
            }
        };
    }, [mergedPdfUrl]);

    const fetchPdf = async () => {
        try {
            const response = await api.getpdf(
                `tms/bca-forms/view-pdf/${payload.template_id}/${payload.batch_student_id}`,
                payload
            );

            const blob = new Blob([response], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            console.log("SINGLE", url);
            setPdfUrl(url);
        } catch (error) {
            console.error("Error fetching PDF:", error);
        }
    };

    const gets3PdfLink = async () => {
        try {
            const response = await api.get(
                `tms/bca-forms/get-s3-file/${payload.template_id}/${payload.batch_student_id}`,
                payload
            );
            setS3Link(response?.data?.full_path ?? null);
        } catch (error) {
            console.error("Error fetching S3 file link", error);
        }
    };

    const checkSignedDocuments = async () => {
        if (!template_id || !studentDropdownData?.length) {
            return;
        }

        setLoadingSignedDocs(true);

        try {
            const allStudentIds = studentDropdownData.map((s) => s.value);

            const checkResponse = await api.post(
                `tms/bca-forms/check-signed-documents/${template_id}`,
                { batch_student_ids: allStudentIds }
            );

            const studentIdsWithSignedDocs =
                checkResponse?.data?.signed_student_ids ||
                checkResponse?.signed_student_ids ||
                [];

            setSignedStudentIds(studentIdsWithSignedDocs);

            if (studentIdsWithSignedDocs.length === 0) {
                setMergedPdfError(
                    "No signed documents found for this template"
                );
            } else {
                setMergedPdfError(null);
            }
        } catch (error) {
            console.error("Error checking signed documents:", error);
            setMergedPdfError("Failed to check signed documents");
        } finally {
            setLoadingSignedDocs(false);
        }
    };

    const fetchMergedSignedPdfs = async (directDownload = true) => {
        if (!template_id || !studentDropdownData?.length) {
            return;
        }

        // Use already-checked signed student IDs
        if (signedStudentIds.length === 0) {
            setMergedPdfError("No signed documents found for this template");
            return;
        }

        setLoadingMergedPdf(true);

        try {
            const response = await api.postPdf(
                `tms/bca-forms/merge-signed-pdfs/${template_id}`,
                {
                    batch_student_ids: signedStudentIds,
                }
            );

            if (response) {
                if (directDownload) {
                    download(response, "forms", "application/pdf");
                } else {
                    // Print functionality 
                    const blob = new Blob([response], {
                        type: "application/pdf",
                    });
                    const blobURL = URL.createObjectURL(blob);

                    const iframe = document.createElement("iframe");
                    iframe.style.position = "fixed";
                    iframe.style.right = "0";
                    iframe.style.bottom = "0";
                    iframe.style.width = "0";
                    iframe.style.height = "0";
                    iframe.style.border = "none";

                    document.body.appendChild(iframe);

                    iframe.onload = function () {
                        iframe.contentWindow.print();

                        setTimeout(() => {
                            document.body.removeChild(iframe);
                            URL.revokeObjectURL(blobURL);
                        }, 1000);
                    };

                    iframe.src = blobURL;
                }
            }
        } catch (error) {
            console.error("Error merging PDFs:", error);
            setMergedPdfError("Failed to merge signed PDFs");
        } finally {
            setLoadingMergedPdf(false);
        }
    };

    return {
        batchData,
        studentDataArray,
        pdfTemplateDropdownData,
        studentDropdownData,
        handleTemplateSelection,
        handleStudentSelection,
        ...studentResource,
        handleSubmit,
        loadSignatures,
        pdfUrl,
        batch_student_id,
        setBatchStudentId,
        template_id,
        setTemplateId,
        payload,
        student_id,
        s3Link,
        setS3Link,
        mergedPdfUrl,
        signedStudentIds,
        loadingMergedPdf,
        mergedPdfError,
        fetchMergedSignedPdfs,
        loadingSignedDocs,
    };
}
